import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApiQuery } from "@/api/useApi";
import type { ReportPage } from "@/api/types";
import { Icon } from "@/components/Icon";
import { ReportCard, ReportListSkeleton } from "@/components/ReportCard";

/**
 * Every report this person may open: their own, those sent to them as manager, and for
 * accounting and payroll everything sent in. The server decides what that is; the search narrows
 * it as they type, by report name, person, manager, department, merchant or category, and marks
 * what matched. The search and page are kept in the address, so coming back from a report shows
 * the same list.
 */
export function AllReportsPage() {
  const [params, setParams] = useSearchParams();
  const term = params.get("q") ?? "";
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [text, setText] = useState(term);

  const show = useCallback(
    (nextTerm: string, nextPage: number) => {
      const next = new URLSearchParams();
      if (nextTerm) next.set("q", nextTerm);
      if (nextPage > 1) next.set("page", String(nextPage));
      setParams(next, { replace: true });
    },
    [setParams],
  );

  // Wait for a short pause in typing so each keystroke isn't a request.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const typed = text.trim();
      if (typed !== term) show(typed, 1);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [text, term, show]);

  const path = `/api/expenses/reports/search?q=${encodeURIComponent(term)}&page=${String(page)}`;
  const { data, error, loading } = useApiQuery<ReportPage>(path);

  // Keep the last results on screen while the next ones load, so the list doesn't flicker.
  // The highlight follows the results on screen, not the box, until the new results arrive.
  const [shown, setShown] = useState<ReportPage>();
  const [shownTerm, setShownTerm] = useState("");
  if (data && data !== shown) {
    setShown(data);
    setShownTerm(term);
  }

  const pages = shown ? Math.max(1, Math.ceil(shown.total / shown.pageSize)) : 1;

  return (
    <div>
      <div className="page-head">
        <h1>All Reports/Search</h1>
      </div>

      <label className="search">
        <Icon name="search" size={18} />
        <input
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Search people, departments, merchants…"
          aria-label="Search reports"
          autoComplete="off"
          enterKeyHint="search"
        />
        {loading && shown && <span className="search-busy" aria-hidden="true" />}
      </label>

      {!shown && loading && <ReportListSkeleton />}
      {error && (
        <div className="error" role="alert">
          {error.message}
        </div>
      )}

      {shown && (
        <p className="muted small search-count" aria-live="polite">
          {shown.total === 0
            ? term
              ? `Nothing matches “${term}”.`
              : "No reports yet."
            : `${String(shown.total)} report${shown.total === 1 ? "" : "s"}`}
        </p>
      )}

      {shown?.items.map((report) => (
        <ReportCard key={report.id} report={report} showSubmitter highlight={shownTerm} />
      ))}

      {pages > 1 && shown && (
        <nav className="pager" aria-label="Report pages">
          <button
            type="button"
            className="link-btn"
            disabled={shown.page <= 1}
            onClick={() => show(term, shown.page - 1)}
          >
            <Icon name="chevronLeft" size={16} /> Newer
          </button>
          <span className="muted">
            Page {shown.page} of {pages}
          </span>
          <button
            type="button"
            className="link-btn"
            disabled={shown.page >= pages}
            onClick={() => show(term, shown.page + 1)}
          >
            Older <Icon name="chevronRight" size={16} />
          </button>
        </nav>
      )}
    </div>
  );
}
